/**
 * SPEC-TrainingSimulator §7.2 / §8.3 — the question controls.
 *
 * ONE set of controls for the key editor (Phase A) and the trainee rail
 * (Phase B). Keying with the trainee's controls is what guarantees the key
 * shape matches the answer shape (D12): every control writes the same
 * AnswerEntry fields `gradeAnswer` reads.
 *
 * Branch semantics (D20): picking a parent pill clears every dependent
 * field, so an un-pick collapses the branch and nothing off-branch is ever
 * submitted. Question TYPES/branches come from QUESTION_DEFS (code); the
 * prompt / labels / option wording come from the question config (data).
 */

import { useMemo, useRef, useState, type ReactNode } from "react";
import { DROP_ZONE_CODES, BAD_REASONS } from "@shared/schema";
import {
  QUESTION_DEFS,
  q10RowsFor,
  differsOptionsFor,
  Q2_IN_POD_NA_LABEL,
  stripEdgePunctuation,
  normalizeReferenceEntry,
  Q7_UNCLEAR_REASONS,
  Q7_UNCLEAR_LABELS,
  DIFFERS_VALUES,
  UNCLEAR_REASONS,
  questionApplies,
  entryComplete,
  isNA as isNAValue,
  type AnswerEntry,
  type AnswerKey,
  type QuestionId,
  type SimulatorQuestionConfig,
} from "@shared/simulator-helpers";
import { DZE_OPTION_LABELS } from "@/lib/prescreen-helpers";
import { Starling } from "@/lib/starling-brand";
import { captureSelectionHighlight, HIGHLIGHT_FIELD_LABEL } from "@/components/simulator/text-highlight";
import {
  MAX_HIGHLIGHTS,
  choiceFeedbackFields,
  feedbackLineId,
  propagateChoiceFeedback,
  pruneFieldRationale,
  SHARED_CHOICE_FIELDS,
  targetLabel,
  type ChoiceFeedbackField,
  type FieldRationale,
  type PhotoBox,
  type TextHighlight,
} from "@/lib/simulator-key-pointers";

/**
 * Key-editor extensions to the entry shape (layout revision 2026-09-09) —
 * shapes and pure helpers live in `@/lib/simulator-key-pointers` so the
 * Phase B runner shares them. All are additive and optional; `gradeAnswer`
 * never reads them.
 *
 *   fieldRationale — feedback per WRONG choice, keyed by the grade field name
 *     (`inPod`, `where`, `rows.street`, …) then by the option the trainee
 *     picked (`note:destination`, `photo:1`, `no`, …). The runner shows
 *     `fieldRationale[field][choice]` when that field grades wrong, falling
 *     back to the question's `rationale`, then to the default feedback.
 *   boxes — bounding boxes drawn on POD photos (`photo:N`) for ANY question,
 *     normalised 0..1 to the photo.
 *   highlights — anchored text on the cards (`{ field, text, occurrence }`)
 *     for ANY question, outlined at render time so they never drift.
 *   valueHighlight — the same anchor shape, captured automatically with the
 *     answer by "Use highlighted text" (q1/q2 value, q7 quote), so the
 *     answer itself is outlined for the trainee; cleared with the value.
 *   The runner must NOT send a question's boxes or highlights to the trainee
 *   until that question has been submitted (Phase 1: with the graded card;
 *   Phase 2: on the review screen); once revealed they stay drawn for the
 *   rest of the item.
 */
/** Short names for the box label chips ("Q1 · Street number"). */
export const QUESTION_SHORT_LABEL: Record<QuestionId, string> = {
  q1_street: "Street number", q2_unit: "Unit", q3_expected_dz: "Expected DZ", q4_actual_dz: "Actual DZ", q5_meets: "Meets expected",
  q6_reference: "Reference photos", q7_return_note: "Return note", q8_missed_return: "Missed return", q9_condition: "Condition",
  q10_decision: "Decision", q11_bad_reason: "Bad reason",
};
export const boxLayerLabel = (id: QuestionId, sort: number) => `Q${sort} · ${QUESTION_SHORT_LABEL[id]}`;

const T = {
  card: "#FFFFFF",
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  sub: "#F3F4F6",
  purple: Starling.purple,
  purpleSub: "rgba(89,47,150,.08)",
  purpleSubB: "rgba(89,47,150,.25)",
  good: "#166534",
  goodBg: "#DCFCE7",
  bad: "#B91C1C",
  badBg: "#FEF2F2",
} as const;

// ─── Primitives ──────────────────────────────────────────────────────────────

export interface PillOption { value: string; label: string }

export function Pills({ options, value, onChange, testId, disabled }: {
  options: PillOption[];
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  testId?: string;
  disabled?: boolean;
}) {
  return (
    <div data-testid={testId} style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            data-value={o.value}
            onClick={() => onChange(active ? null : o.value)}
            style={{
              padding: "5px 12px",
              borderRadius: 999,
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: Starling.fontBody,
              cursor: disabled ? "not-allowed" : "pointer",
              border: `1px solid ${active ? T.purple : T.border}`,
              background: active ? T.purple : T.card,
              color: active ? "#fff" : T.text,
              opacity: disabled ? 0.6 : 1,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export interface EvidenceOption { value: string; label: string; thumbUrl?: string }

/** D10 — multi-select across Photo 1..N and the Completion note. */
export function EvidenceChips({ options, value, onChange, testId }: {
  options: EvidenceOption[];
  value: string[] | undefined;
  onChange: (next: string[]) => void;
  testId?: string;
}) {
  const selected = new Set(value ?? []);
  return (
    <div data-testid={testId} style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {options.map((o) => {
        const active = selected.has(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            data-value={o.value}
            onClick={() => {
              const next = new Set(selected);
              if (active) next.delete(o.value); else next.add(o.value);
              onChange(options.map((x) => x.value).filter((v) => next.has(v)));
            }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: o.thumbUrl ? "3px 10px 3px 3px" : "4px 10px",
              borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: Starling.fontBody, cursor: "pointer",
              border: `1px solid ${active ? T.purple : T.border}`,
              background: active ? T.purpleSub : T.card,
              color: active ? T.purple : T.text,
              boxShadow: active ? `inset 0 0 0 1px ${T.purple}` : "none",
            }}
          >
            {o.thumbUrl && (
              <span style={{ width: 26, height: 26, borderRadius: 5, background: `#E5E7EB url(${o.thumbUrl}) center/cover`, display: "inline-block" }} />
            )}
            {active ? "✓ " : ""}{o.label}
          </button>
        );
      })}
    </div>
  );
}

export function DropZoneSelect({ value, onChange, testId }: {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  testId?: string;
}) {
  return (
    <select
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      style={{
        padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13,
        fontFamily: Starling.fontBody, background: T.card, color: T.text, minWidth: 260,
      }}
    >
      <option value="">— pick a drop zone —</option>
      {DROP_ZONE_CODES.map((code) => (
        <option key={code} value={code}>{code} · {DZE_OPTION_LABELS[code]}</option>
      ))}
    </select>
  );
}

/**
 * Highlight capture — q7 (Completion Details card) and q1/q2 (Delivery
 * Details card). The trainee highlights text in the card and presses "Use
 * highlighted text"; the substring is captured into the entry.
 * `sourceElementId` scopes the capture to that card so a stray selection
 * elsewhere on the page is ignored. There is deliberately NO typed
 * fallback: the value must come from the on-screen text, and any highlight
 * inside the card is accepted — a wrong one is graded wrong and the
 * feedback shows the keyed value (Sarah, 2026-09-09).
 */
export function QuoteCapture({ value, onChange, sourceElementId, sourceLabel = "Completion Details", testId, trimEdgePunctuation = false }: {
  value: string | undefined;
  /** For the q1/q2 value: drop punctuation the selection dragged in from the address line ("1949," → "1949"). Off for the q7 quote, which is kept verbatim. */
  trimEdgePunctuation?: boolean;
  /** `highlight` is the anchor for the same selection (null when cleared or unresolvable) — it becomes the trainee's outline. */
  onChange: (next: string, highlight: TextHighlight | null) => void;
  sourceElementId?: string;
  /** Card the selection must come from — used in the hints ("Completion Details", "Delivery Details"). */
  sourceLabel?: string;
  testId?: string;
}) {
  const [hint, setHint] = useState<string | null>(null);
  const capture = () => {
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    const raw = sel?.toString().trim() ?? "";
    const text = trimEdgePunctuation ? stripEdgePunctuation(raw) : raw;
    if (!text) { setHint(`Highlight some text in the ${sourceLabel} card first.`); return; }
    if (sourceElementId && sel && sel.rangeCount > 0) {
      const root = document.getElementById(sourceElementId);
      const node = sel.getRangeAt(0).commonAncestorContainer;
      if (root && !root.contains(node)) { setHint(`Highlight text inside the ${sourceLabel} card.`); return; }
    }
    const anchored = sourceElementId ? captureSelectionHighlight([sourceElementId]) : null;
    if (anchored && "error" in anchored) setHint(`Answer saved, but the trainee will get no outline — ${anchored.error}`);
    else setHint(null);
    onChange(text, anchored && "highlight" in anchored ? anchored.highlight : null);
  };
  return (
    <div data-testid={testId} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={capture}
          data-testid={testId ? `${testId}-capture` : undefined}
          style={{
            padding: "5px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, fontFamily: Starling.fontBody,
            border: `1px solid ${T.purple}`, background: T.purpleSub, color: T.purple, cursor: "pointer",
          }}
        >
          Use highlighted text
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("", null)}
            data-testid={testId ? `${testId}-clear` : undefined}
            style={{ background: "none", border: "none", color: T.txt2, fontSize: 12, cursor: "pointer", padding: 0, fontFamily: Starling.fontBody, textDecoration: "underline" }}
          >
            Clear
          </button>
        )}
        {hint && <span style={{ fontSize: 12, color: T.bad }}>{hint}</span>}
      </div>
      <div
        data-testid={testId ? `${testId}-value` : undefined}
        data-value={value ?? ""}
        style={{
          fontSize: 12.5, fontStyle: value ? "italic" : "normal", color: value ? T.text : T.txt3,
          background: value ? "#FEF9C3" : T.sub, padding: "5px 8px", borderRadius: 4, minHeight: 26, boxSizing: "border-box",
        }}
      >
        {value ? `“${value}”` : `Nothing highlighted yet — select the text in the ${sourceLabel} card, then press the button.`}
      </div>
    </div>
  );
}

function TextInput({ value, onChange, placeholder, testId, width }: {
  value: string | null | undefined; onChange: (v: string) => void; placeholder?: string; testId?: string; width?: number;
}) {
  return (
    <input
      type="text"
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: Starling.fontBody, width: width ?? 180 }}
    />
  );
}

function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.txt2, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</div>
      {children}
    </div>
  );
}

/** Purple-tinted block that holds branch fields once a pill is picked (§8.3). */
function Branch({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div
      data-testid={testId}
      style={{ background: T.purpleSub, border: `1px solid ${T.purpleSubB}`, borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10 }}
    >
      {children}
    </div>
  );
}

// ─── Per-question form ───────────────────────────────────────────────────────

const yesNo = (opts: Record<string, string> | undefined): PillOption[] => [
  { value: "yes", label: opts?.yes ?? "Yes" },
  { value: "no", label: opts?.no ?? "No" },
];
const yesNoUnclear = (opts: Record<string, string> | undefined): PillOption[] => [
  ...yesNo(opts),
  { value: "unclear", label: opts?.unclear ?? "Unclear" },
];
/** q7's note-specific Unclear reasons; labels fall back to the code defaults when the stored config predates them. */
const q7UnclearReasons = (opts: Record<string, string> | undefined): PillOption[] =>
  Q7_UNCLEAR_REASONS.map((r) => ({ value: r, label: opts?.[r] ?? Q7_UNCLEAR_LABELS[r] }));
const unclearReasons = (opts: Record<string, string> | undefined): PillOption[] =>
  UNCLEAR_REASONS.map((r) => ({ value: r, label: opts?.[r] ?? (r === "pod_missing" ? "POD missing" : "Can't interpret POD") }));

/**
 * Pointers for the trainee — photo boxes (drawn on the Photos card) and text
 * highlights (captured from a selection on the Delivery / Completion cards).
 */
function PointerSummary({ id, boxes, highlights, valueHighlight, onChange, rootIds }: {
  id: QuestionId;
  boxes: PhotoBox[] | undefined;
  highlights: TextHighlight[] | undefined;
  /** Anchor captured with the answer itself ("Use highlighted text") — outlined for the trainee, cleared with the value. */
  valueHighlight: TextHighlight | null | undefined;
  onChange: (patch: { boxes?: PhotoBox[]; highlights?: TextHighlight[] }) => void;
  rootIds: string[];
}) {
  const [hint, setHint] = useState<string | null>(null);
  const boxList = Array.isArray(boxes) ? boxes : [];
  const hlList = Array.isArray(highlights) ? highlights : [];
  const perTarget = new Map<string, number>();
  for (const b of boxList) perTarget.set(b.target, (perTarget.get(b.target) ?? 0) + 1);
  const boxSummary = Array.from(perTarget.entries()).map(([t, n]) => `${targetLabel(t)}${n > 1 ? ` ×${n}` : ""}`).join(" · ");
  const addHighlight = () => {
    const r = captureSelectionHighlight(rootIds);
    if ("error" in r) { setHint(r.error); return; }
    setHint(null);
    const dup = hlList.some((h) => h.field === r.highlight.field && h.text === r.highlight.text && h.occurrence === r.highlight.occurrence);
    if (dup) return;
    if (hlList.length >= MAX_HIGHLIGHTS) { setHint(`At most ${MAX_HIGHLIGHTS} highlights per question.`); return; }
    onChange({ highlights: [...hlList, r.highlight] });
  };
  return (
    <div data-testid={`q-${id}-pointers`} data-boxes={boxList.length} data-highlights={hlList.length} style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: T.txt2 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700 }}>▭</span>
        <span data-testid={`q-${id}-boxes`} data-count={boxList.length}>
          {boxList.length === 0
            ? "No photo box yet — drag on a photo to show the trainee where to look."
            : <><strong style={{ color: T.text }}>{boxList.length} photo box{boxList.length === 1 ? "" : "es"}</strong> · {boxSummary}</>}
        </span>
        {boxList.length > 0 && (
          <button type="button" onClick={() => onChange({ boxes: [] })} data-testid={`q-${id}-boxes-clear`} style={{ background: "none", border: "none", color: T.bad, fontSize: 12, cursor: "pointer", padding: 0, fontFamily: Starling.fontBody }}>
            Clear boxes
          </button>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700 }}>✎</span>
        <button
          type="button"
          onClick={addHighlight}
          data-testid={`q-${id}-highlight-add`}
          style={{ padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: Starling.fontBody, border: `1px solid ${T.purple}`, background: T.purpleSub, color: T.purple, cursor: "pointer" }}
        >
          Highlight selected text for the trainee
        </button>
        {hint && <span style={{ color: T.bad }}>{hint}</span>}
        {!hint && hlList.length === 0 && !valueHighlight && <span>Select text in Delivery Details or Completion Details first.</span>}
      </div>
      {(hlList.length > 0 || valueHighlight) && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid={`q-${id}-highlights`}>
          {valueHighlight && (
            <span data-testid={`q-${id}-value-highlight`} title="Captured with the answer — cleared when the answer is cleared" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 6, background: "#FEF9C3", color: T.text, border: `1px solid #86EFAC` }}>
              <span style={{ color: T.good, fontWeight: 700 }}>Answer</span>
              <span style={{ color: T.txt3 }}>{HIGHLIGHT_FIELD_LABEL[valueHighlight.field]}{valueHighlight.occurrence > 1 ? ` #${valueHighlight.occurrence}` : ""}</span>
              <span style={{ fontStyle: "italic" }}>“{valueHighlight.text}”</span>
            </span>
          )}
          {hlList.map((h, i) => (
            <span key={`${h.field}-${h.text}-${h.occurrence}`} data-testid={`q-${id}-highlight`} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 6, background: "#FEF9C3", color: T.text, border: `1px solid ${T.border}` }}>
              <span style={{ color: T.txt3 }}>{HIGHLIGHT_FIELD_LABEL[h.field]}{h.occurrence > 1 ? ` #${h.occurrence}` : ""}</span>
              <span style={{ fontStyle: "italic" }}>“{h.text}”</span>
              <button type="button" aria-label="Remove highlight" onClick={() => onChange({ highlights: hlList.filter((_, j) => j !== i) })} style={{ background: "none", border: "none", color: T.bad, cursor: "pointer", padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Per-choice feedback (key editor only) ───────────────────────────────────

export function ChoiceFeedback({ fields, value, onChange, onCommit, testId }: {
  fields: ChoiceFeedbackField[];
  value: FieldRationale | undefined;
  onChange: (next: FieldRationale | undefined) => void;
  /**
   * Fired when a line is left (blur) with a different text than it had on
   * focus — the key form copies shared lines to the delivery's other
   * questions at that moment (`propagateChoiceFeedback`), not per keystroke.
   */
  onCommit?: (field: string, choice: string, text: string) => void;
  testId?: string;
}) {
  const focusText = useRef<string>("");
  if (!fields.length) return null;
  const fr = value ?? {};
  const setOne = (field: string, choice: string, text: string) =>
    onChange(pruneFieldRationale({ ...fr, [field]: { ...(fr[field] ?? {}), [choice]: text } }));
  const shared = fields.some((f) => f.field in SHARED_CHOICE_FIELDS);
  return (
    <div data-testid={testId} style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: `1px dashed ${T.border}`, paddingTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.txt2, textTransform: "uppercase", letterSpacing: "0.03em" }}>
        If the trainee picks… <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: T.txt3 }}>(blank → the rationale above)</span>
      </div>
      {shared && onCommit && (
        <div data-testid={testId ? `${testId}-shared-hint` : undefined} style={{ fontSize: 11.5, color: T.txt3, marginTop: -4 }}>
          Photo, note and POD lines are copied into the blank lines of this delivery's other questions when you leave the box. Copies made here follow your edits; anything already written is kept.
        </div>
      )}
      {fields.map((f) => (
        <div key={f.field} data-testid={testId ? `${testId}-${f.field}` : undefined} style={{ display: "grid", gridTemplateColumns: "minmax(110px, auto) 1fr", gap: "4px 10px", alignItems: "center" }}>
          <div style={{ gridColumn: "1 / -1", fontSize: 11.5, color: T.txt3 }}>{f.label}</div>
          {f.options.map((o) => (
            <div key={o.value} style={{ display: "contents" }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={o.label}>{o.label}</span>
              <input
                type="text"
                value={fr[f.field]?.[o.value] ?? ""}
                placeholder="Why this is wrong…"
                data-testid={testId ? `${testId}-${f.field}-${o.value}` : undefined}
                onChange={(ev) => setOne(f.field, o.value, ev.target.value)}
                onFocus={(ev) => { focusText.current = ev.target.value; }}
                onBlur={(ev) => { if (onCommit && ev.target.value !== focusText.current) onCommit(f.field, o.value, ev.target.value); }}
                style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12.5, fontFamily: Starling.fontBody, minWidth: 0 }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export interface QuestionFormProps {
  config: SimulatorQuestionConfig;
  entry: AnswerEntry | null | undefined;
  onChange: (next: AnswerEntry | null) => void;
  evidence: EvidenceOption[];
  /** Full key — q8 visibility depends on q7. */
  answerKey: AnswerKey;
  completionNoteElementId?: string;
  /** Wraps the Delivery Details card — q1/q2 capture the street/unit by highlighting text there. */
  deliveryDetailsElementId?: string;
  /** Key editor shows a rationale box per question; the trainee rail does not. */
  showRationale?: boolean;
  /** Key editor: a per-choice feedback line was committed (see `ChoiceFeedback.onCommit`). */
  onChoiceFeedbackCommit?: (field: string, choice: string, text: string) => void;
  /**
   * Override for "is this question asked". The key editor derives it from the
   * key being edited (q8 ⇔ key q7 ≠ Yes); the trainee rail passes the
   * server's `asked` verdict instead, because it is the KEY's q7 that decides,
   * never the trainee's own answer (Phase B audit W1).
   */
  applies?: boolean;
}

export function QuestionForm({ config, entry, onChange, evidence, answerKey, completionNoteElementId, deliveryDetailsElementId, showRationale, onChoiceFeedbackCommit, applies: appliesProp }: QuestionFormProps) {
  const id = config.id;
  const e = (entry ?? {}) as any;
  const labels = config.text.labels ?? {};
  const options = config.text.options ?? {};
  const set = (patch: Record<string, unknown>) => onChange({ ...e, ...patch });
  const tid = (suffix: string) => `q-${id}-${suffix}`;

  const applies = appliesProp ?? questionApplies(id, answerKey);
  if (!applies) {
    return <div style={{ fontSize: 13, color: T.txt3, fontStyle: "italic" }}>Not asked — the driver said returns were collected (q7 = Yes).</div>;
  }

  let body: ReactNode = null;
  switch (id) {
    case "q1_street":
    case "q2_unit": {
      const isUnit = id === "q2_unit";
      // N/A ends the question: with no unit to look for, the POD sub-questions
      // are neither shown nor required (shared rule in `entryComplete`).
      const isNA = isUnit && isNAValue(e.value);
      body = (
        <>
          <Field label={isUnit ? "Expected unit — highlight it in the Delivery Details card" : "Expected street number — highlight it in the Delivery Details card"}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <QuoteCapture
                  value={isNA ? "" : (e.value ?? "")}
                  sourceElementId={deliveryDetailsElementId}
                  sourceLabel="Delivery Details"
                  trimEdgePunctuation
                  testId={tid("value")}
                  onChange={(v, h) => set({ value: v, valueHighlight: h ?? undefined })}
                />
              </div>
              {isUnit && (
                <Pills options={[{ value: "N/A", label: labels.na ?? "N/A" }]} value={isNA ? "N/A" : null} testId={tid("na")}
                  onChange={(v) => onChange({ ...e, value: v ? "N/A" : "", valueHighlight: undefined, inPod: undefined, where: undefined, otherSeen: undefined, otherValue: undefined, unclearReason: undefined })} />
              )}
            </div>
          </Field>
          {!isNA && (
          <Field label={labels.inPod ?? "Is it visible in the POD?"}>
            <Pills options={isUnit ? [...yesNoUnclear(options), { value: "na", label: options.inPodNa ?? Q2_IN_POD_NA_LABEL }] : yesNoUnclear(options)} value={e.inPod} testId={tid("inPod")}
              onChange={(v) => onChange({ ...e, inPod: v ?? undefined, where: undefined, otherSeen: undefined, otherValue: undefined, unclearReason: undefined })} />
          </Field>
          )}
          {!isNA && e.inPod === "yes" && (
            <Branch testId={tid("branch")}>
              <Field label={labels.where ?? "Where do you see it?"}>
                <EvidenceChips options={evidence} value={e.where} testId={tid("where")} onChange={(w) => set({ where: w })} />
              </Field>
            </Branch>
          )}
          {!isNA && e.inPod === "no" && (
            <Branch testId={tid("branch")}>
              <Field label={labels.otherSeen ?? "Do you see a different value?"}>
                <Pills options={yesNo(options)} value={e.otherSeen} testId={tid("otherSeen")}
                  onChange={(v) => onChange({ ...e, otherSeen: v ?? undefined, otherValue: undefined, where: undefined })} />
              </Field>
              {e.otherSeen === "yes" && (
                <>
                  <Field label={labels.otherValue ?? "Which value?"}>
                    <TextInput value={e.otherValue} testId={tid("otherValue")} onChange={(v) => set({ otherValue: v })} />
                  </Field>
                  <Field label={labels.where ?? "Where do you see it?"}>
                    <EvidenceChips options={evidence} value={e.where} testId={tid("where")} onChange={(w) => set({ where: w })} />
                  </Field>
                </>
              )}
            </Branch>
          )}
          {!isNA && e.inPod === "unclear" && (
            <Branch testId={tid("branch")}>
              <Field label={labels.unclearReason ?? "Why is it unclear?"}>
                <Pills options={unclearReasons(options)} value={e.unclearReason} testId={tid("unclearReason")} onChange={(v) => set({ unclearReason: v ?? undefined })} />
              </Field>
              <Field label={labels.where ?? "Where (optional)"}>
                <EvidenceChips options={evidence} value={e.where} testId={tid("where")} onChange={(w) => set({ where: w })} />
              </Field>
            </Branch>
          )}
        </>
      );
      break;
    }
    case "q3_expected_dz":
      body = <DropZoneSelect value={e.value} testId={tid("value")} onChange={(v) => set({ value: v })} />;
      break;
    case "q4_actual_dz":
      body = (
        <>
          <DropZoneSelect value={e.value} testId={tid("value")} onChange={(v) => set({ value: v })} />
          <Field label={labels.where ?? "Which photos show it?"}>
            <EvidenceChips options={evidence} value={e.where} testId={tid("where")} onChange={(w) => set({ where: w })} />
          </Field>
        </>
      );
      break;
    case "q5_meets":
      body = (
        <>
          <Pills options={yesNo(options)} value={e.value} testId={tid("value")}
            onChange={(v) => onChange({ ...e, value: v ?? undefined, clearWhy: undefined, reason: undefined })} />
          {e.value === "no" && (
            <Branch testId={tid("branch")}>
              <Field label={labels.clearWhy ?? "Is it clear why the driver deviated?"}>
                <Pills options={yesNo(options)} value={e.clearWhy} testId={tid("clearWhy")} onChange={(v) => set({ clearWhy: v ?? undefined })} />
              </Field>
            </Branch>
          )}
        </>
      );
      break;
    case "q6_reference": {
      // Legacy `similar` keys read as `conflict` (yes → no conflict; no → unanswered, re-key).
      const r = normalizeReferenceEntry(e) as any;
      const { similar: _legacy, ...base } = r;
      body = (
        <>
          <Pills options={yesNo(options)} value={r.value} testId={tid("value")}
            onChange={(v) => onChange({ ...base, value: v ?? undefined, conflict: undefined, differs: undefined })} />
          {r.value === "yes" && (
            <Branch testId={tid("branch")}>
              <Field label={labels.conflict ?? "Do the verified past photos show this delivery at the wrong street, unit or drop zone? A past photo that simply doesn't show something is not a conflict."}>
                <Pills options={yesNo(options)} value={r.conflict} testId={tid("conflict")}
                  onChange={(v) => onChange({ ...base, conflict: v ?? undefined, differs: undefined })} />
              </Field>
              {r.conflict === "yes" && (
                <Field label={labels.differs ?? "Which looks wrong?"}>
                  <EvidenceChips
                    options={differsOptionsFor(answerKey).map((d) => ({ value: d, label: options[d] ?? d }))}
                    value={r.differs}
                    testId={tid("differs")}
                    onChange={(d) => onChange({ ...base, differs: d })}
                  />
                </Field>
              )}
            </Branch>
          )}
        </>
      );
      break;
    }
    case "q7_return_note":
      body = (
        <>
          <Pills options={yesNoUnclear(options)} value={e.value} testId={tid("value")}
            onChange={(v) => onChange({ ...e, value: v ?? undefined, quote: undefined, unclearReason: undefined })} />
          {(e.value === "yes" || e.value === "no") && (
            <Branch testId={tid("branch")}>
              <Field label={e.value === "no" ? (labels.quoteNo ?? "Highlight the driver's words if the note says so (optional)") : (labels.quote ?? "Highlight the words in the completion note")}>
                <QuoteCapture value={e.quote} sourceElementId={completionNoteElementId} testId={tid("quote")} onChange={(q, h) => set({ quote: q, valueHighlight: h ?? undefined })} />
              </Field>
            </Branch>
          )}
          {e.value === "unclear" && (
            <Branch testId={tid("branch")}>
              <Field label={labels.unclearReason ?? "Why is it unclear?"}>
                <Pills options={q7UnclearReasons(options)} value={e.unclearReason} testId={tid("unclearReason")} onChange={(v) => set({ unclearReason: v ?? undefined })} />
              </Field>
            </Branch>
          )}
        </>
      );
      break;
    case "q8_missed_return":
      body = (
        <>
          <Pills options={yesNo(options)} value={e.value} testId={tid("value")}
            onChange={(v) => onChange({ ...e, value: v ?? undefined, where: undefined })} />
          {e.value === "yes" && (
            <Branch testId={tid("branch")}>
              <Field label={labels.where ?? "Which photos?"}>
                <EvidenceChips options={evidence} value={e.where} testId={tid("where")} onChange={(w) => set({ where: w })} />
              </Field>
            </Branch>
          )}
        </>
      );
      break;
    case "q9_condition":
      body = <Pills options={yesNo(options)} value={e.value} testId={tid("value")} onChange={(v) => set({ value: v ?? undefined })} />;
      break;
    case "q10_decision": {
      const rows = e.rows ?? {};
      const rowOpts: PillOption[] = [
        { value: "yes", label: options.yes ?? "Yes" },
        { value: "no", label: options.no ?? "No" },
        { value: "unsure", label: options.unsure ?? "Unsure" },
      ];
      // The unit row alone takes N/A: the task has a unit, but the drop zone
      // (front desk, lobby…) is not at its door, so the photos cannot show it.
      const unitRowOpts: PillOption[] = [...rowOpts, { value: "na", label: options.na ?? "N/A" }];
      const rowLabel: Record<string, string> = { street: labels.street ?? "Street number correct?", unit: labels.unit ?? "Unit correct?", dz: labels.dz ?? "Drop zone OK?", returns: labels.returns ?? "Returns OK?" };
      // No unit on this delivery (q2 = N/A) → no "Unit correct?" row to answer.
      const askedRows = q10RowsFor(answerKey);
      body = (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 14px", alignItems: "center" }}>
            {askedRows.map((r) => (
              <div key={r} style={{ display: "contents" }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{rowLabel[r]}</div>
                <Pills options={r === "unit" ? unitRowOpts : rowOpts} value={rows[r]} testId={tid(`row-${r}`)} onChange={(v) => set({ rows: { ...rows, [r]: v ?? undefined } })} />
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: T.txt2 }}>{labels.rule ?? "All Yes (unit may be N/A) → Good · any No or Unsure → Bad"}</div>
          <div style={{ display: "flex", gap: 10 }} data-testid={tid("decision")}>
            {(["GOOD", "BAD"] as const).map((d) => {
              const active = e.decision === d;
              const good = d === "GOOD";
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={active}
                  data-value={d}
                  onClick={() => set({ decision: active ? undefined : d })}
                  style={{
                    padding: "9px 26px", borderRadius: 8, fontSize: 14, fontWeight: 700, fontFamily: Starling.fontBody, cursor: "pointer",
                    border: `1px solid ${good ? T.good : T.bad}`,
                    background: active ? (good ? T.good : T.bad) : (good ? T.goodBg : T.badBg),
                    color: active ? "#fff" : (good ? T.good : T.bad),
                  }}
                >
                  {good ? "✓ Good" : "✗ Bad"}
                </button>
              );
            })}
          </div>
        </>
      );
      break;
    }
    case "q11_bad_reason":
      body = (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <select
            data-testid={tid("value")}
            value={e.value ?? ""}
            onChange={(ev) => set({ value: ev.target.value || null })}
            style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: Starling.fontBody, maxWidth: 260 }}
          >
            <option value="">— reserved (deferred) —</option>
            {BAD_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <span style={{ fontSize: 12, color: T.txt3 }}>Wording and required phases are still open (spec §12.1). Keying it now is optional.</span>
        </div>
      );
      break;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid={`question-form-${id}`}>
      {body}
      {showRationale && (
        <>
          <PointerSummary
            id={id}
            boxes={e.boxes}
            highlights={e.highlights}
            valueHighlight={e.valueHighlight}
            rootIds={[deliveryDetailsElementId, completionNoteElementId].filter((x): x is string => !!x)}
            onChange={(patch) => set(patch)}
          />
          <Field label="Fallback rationale — shown when wrong and no line below matches (optional)">
            <textarea
              value={e.rationale ?? ""}
              onChange={(ev) => set({ rationale: ev.target.value })}
              data-testid={tid("rationale")}
              rows={2}
              placeholder={`Explain the correct answer, e.g. where the value is. Blank → default: “${config.text.feedbackDefault.wrong}”`}
              style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: Starling.fontBody, resize: "vertical" }}
            />
          </Field>
          <ChoiceFeedback
            fields={choiceFeedbackFields(id, e, evidence, labels, options, answerKey)}
            value={e.fieldRationale}
            testId={tid("choice-feedback")}
            onChange={(fr) => {
              const next: any = { ...e, fieldRationale: fr };
              if (!fr) delete next.fieldRationale;
              onChange(next);
            }}
            onCommit={onChoiceFeedbackCommit}
          />
        </>
      )}
    </div>
  );
}

// ─── Whole-key form (Q1–Q11) ─────────────────────────────────────────────────

export interface AnswerKeyFormProps {
  questions: SimulatorQuestionConfig[];
  answerKey: AnswerKey;
  onChange: (next: AnswerKey) => void;
  evidence: EvidenceOption[];
  completionNoteElementId?: string;
  deliveryDetailsElementId?: string;
  /** The item's eligible phases — a question reads complete when it is complete for every one of them. */
  phases?: number[];
  /** Carousel position (index into the sorted questions). Controlled so the page can key photo boxes off the active question. */
  activeIndex: number;
  onActiveIndexChange: (next: number) => void;
}

/**
 * One question at a time (layout revision 2026-09-09): a stepper strip of
 * Q1..Q11 with completion colour, ‹ › arrows, and the active question card.
 * Stacking all eleven cards next to the delivery clone forced a long scroll.
 */
export function AnswerKeyForm({ questions, answerKey, onChange, evidence, completionNoteElementId, deliveryDetailsElementId, phases, activeIndex, onActiveIndexChange }: AnswerKeyFormProps) {
  const sorted = useMemo(() => [...questions].sort((a, b) => a.sort - b.sort), [questions]);
  // Feedback lines this session copied from another question (`feedbackLineId`).
  // Only these follow later edits of their source; a reload forgets them, so
  // a saved line is then "already written" and is left alone.
  const mirrors = useRef<Set<string>>(new Set());
  const completeOf = (q: SimulatorQuestionConfig) => {
    const applies = questionApplies(q.id, answerKey);
    if (!applies) return true;
    return phases && phases.length
      ? phases.every((phase) => entryComplete(q.id, answerKey[q.id] ?? null, { key: answerKey, phase }))
      : entryComplete(q.id, answerKey[q.id] ?? null, { key: answerKey });
  };
  const idx = Math.min(Math.max(activeIndex, 0), Math.max(sorted.length - 1, 0));
  const q = sorted[idx];
  if (!q) return null;
  const def = QUESTION_DEFS[q.id];
  const applies = questionApplies(q.id, answerKey);
  const complete = completeOf(q);
  const phaseLabel = q.mandatoryPhases.length ? `Phases ${q.mandatoryPhases.join(", ")}` : "Not required";
  const doneCount = sorted.filter((x) => x.enabled && completeOf(x)).length;
  const enabledCount = sorted.filter((x) => x.enabled).length;
  const arrow = (dir: -1 | 1, disabled: boolean) => (
    <button
      type="button"
      aria-label={dir < 0 ? "Previous question" : "Next question"}
      data-testid={dir < 0 ? "key-prev" : "key-next"}
      disabled={disabled}
      onClick={() => onActiveIndexChange(idx + dir)}
      style={{
        width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.card, color: disabled ? T.txt3 : T.purple,
        fontSize: 18, fontWeight: 700, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}
    >
      {dir < 0 ? "‹" : "›"}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="answer-key-form">
      {/* Stepper strip */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {arrow(-1, idx === 0)}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", flex: 1, justifyContent: "center" }} data-testid="key-stepper">
          {sorted.map((x, i) => {
            const active = i === idx;
            const xApplies = questionApplies(x.id, answerKey);
            const done = completeOf(x);
            const bg = !x.enabled ? T.sub : !xApplies ? T.sub : done ? T.goodBg : T.card;
            const fg = !x.enabled ? T.txt3 : !xApplies ? T.txt3 : done ? T.good : T.txt2;
            return (
              <button
                key={x.id}
                type="button"
                data-testid={`key-step-${x.id}`}
                data-complete={done ? "true" : "false"}
                aria-current={active ? "step" : undefined}
                title={`Q${x.sort} · ${x.text.prompt}`}
                onClick={() => onActiveIndexChange(i)}
                style={{
                  minWidth: 28, height: 26, padding: "0 6px", borderRadius: 6, fontSize: 11.5, fontWeight: 700, fontFamily: Starling.fontBody, cursor: "pointer",
                  background: bg, color: active ? T.purple : fg,
                  border: `1px solid ${active ? T.purple : done && xApplies && x.enabled ? "#86EFAC" : T.border}`,
                  boxShadow: active ? `0 0 0 2px ${T.purpleSubB}` : "none",
                }}
              >
                {x.sort}
              </button>
            );
          })}
        </div>
        {arrow(1, idx === sorted.length - 1)}
      </div>
      <div style={{ fontSize: 11.5, color: T.txt3, textAlign: "center" }} data-testid="key-progress">
        Question {idx + 1} of {sorted.length} · {doneCount}/{enabledCount} keyed
      </div>

      {/* Active question card */}
      <div
        key={q.id}
        data-testid={`key-question-${q.id}`}
        data-complete={complete ? "true" : "false"}
        style={{
          background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px",
          opacity: q.enabled ? 1 : 0.55, borderLeft: `4px solid ${complete ? T.good : applies ? T.txt3 : T.border}`,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
          <div style={{ fontFamily: Starling.fontHead, fontWeight: 600, fontSize: 14 }}>
            <span style={{ color: T.purple, marginRight: 8 }}>Q{q.sort}</span>{q.text.prompt}
          </div>
          <div style={{ fontSize: 11, color: T.txt3, whiteSpace: "nowrap" }}>{def.type} · {phaseLabel}{q.enabled ? "" : " · disabled"}</div>
        </div>
        <QuestionForm
          config={q}
          entry={answerKey[q.id] ?? null}
          onChange={(next) => onChange({ ...answerKey, [q.id]: next })}
          evidence={evidence}
          answerKey={answerKey}
          completionNoteElementId={completionNoteElementId}
          deliveryDetailsElementId={deliveryDetailsElementId}
          showRationale
          onChoiceFeedbackCommit={(field, choice, text) => {
            // Sarah, 2026-09-11: one "why this choice is wrong" line per
            // delivery, not per question — copy it where it is still blank,
            // and into the copies this session made. A line edited by hand
            // here stops being a copy.
            mirrors.current.delete(feedbackLineId(q.id, field, choice));
            const r = propagateChoiceFeedback(answerKey, q.id, field, choice, text, mirrors.current);
            for (const qid of r.written) mirrors.current.add(feedbackLineId(qid, field, choice));
            if (r.key !== answerKey) onChange(r.key);
          }}
        />
      </div>

      {/* Bottom nav — same arrows, so the hand never has to travel back up after a long card. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button type="button" data-testid="key-prev-bottom" disabled={idx === 0} onClick={() => onActiveIndexChange(idx - 1)} style={navBtn(idx === 0)}>‹ Previous</button>
        <button type="button" data-testid="key-next-bottom" disabled={idx === sorted.length - 1} onClick={() => onActiveIndexChange(idx + 1)} style={navBtn(idx === sorted.length - 1)}>Next ›</button>
      </div>
    </div>
  );
}

function navBtn(disabled: boolean): React.CSSProperties {
  return {
    padding: "6px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.card, color: disabled ? T.txt3 : T.purple,
    fontSize: 12.5, fontWeight: 700, fontFamily: Starling.fontBody, cursor: disabled ? "default" : "pointer",
  };
}

/**
 * Build the D10 evidence list for a snapshot: Photo 1..N and the Completion
 * note. Destination notes are deliberately not offered (2026-09-15, Sarah):
 * they never verify anything, so every key carried the same "can't be used"
 * line against that chip.
 */
export function evidenceOptionsFor(snapshot: { podPhotos?: Array<{ index: number; url: string }>; driverCompletionNotes?: string | null; destinationNotes?: string | null }): EvidenceOption[] {
  const photos = (snapshot.podPhotos ?? []).map((p) => ({ value: `photo:${p.index}`, label: `Photo ${p.index}`, thumbUrl: p.url }));
  const notes: EvidenceOption[] = [];
  if (snapshot.driverCompletionNotes) notes.push({ value: "note:completion", label: "Completion note" });
  return [...photos, ...notes];
}
